import AssignmentIcon from '@mui/icons-material/Assignment';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PersonIcon from '@mui/icons-material/Person';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import * as React from 'react';
import { ListProvasRespondidasProps } from '../../questionnaire/types/questionnaire.types';

interface ProvaSelectorProps {
  provas: ListProvasRespondidasProps[];
  isLoading: boolean;
  onSelect: (eventUId: string) => void;
}

export const ProvaSelector: React.FC<ProvaSelectorProps> = ({
  provas,
  isLoading,
  onSelect,
}) => {
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  const isSingle = provas.length === 1;

  return (
    <Box sx={{ mt: 14, mb: 4, px: 2 }}>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 2,
          p: 1,
          mb: 3,
          // mx: 2,
          textAlign: 'center',
        }}
      >
        <Typography
          sx={{
            color: '#ffffff',
            fontSize: { xs: '11pt', md: '12pt' },
            fontFamily: 'Roboto-Bold !important',
            letterSpacing: '1px',
            textTransform: 'uppercase',
          }}
        >
          Visualização das Respostas
        </Typography>
      </Box>

      {provas.length === 0 ? (
        <Box textAlign="center" py={6}>
          <AssignmentIcon
            sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }}
          />
          <Typography variant="body2" color="text.secondary">
            Nenhuma prova encontrada.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isSingle
              ? 'minmax(0, 600px)'
              : 'repeat(auto-fit, minmax(260px, 1fr))',
            justifyContent: isSingle ? 'center' : 'unset',
            gap: 1.5,
            pt: 2,
          }}
        >
          {provas
            .slice()
            .sort((a, b) => a.idProva - b.idProva)
            .map((prova) => (
              <Card
                key={prova.eventUId}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  transition: 'border-color 0.15s',
                  '&:hover': { borderColor: 'text.primary' },
                }}
              >
                <CardActionArea onClick={() => onSelect(prova.eventUId)}>
                  <CardContent
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                  >
                    {/* Top row: icon + title + arrow */}
                    <Box display="flex" alignItems="flex-start" gap={1.5}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1.5,
                          bgcolor: '#EEEDFE',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <EditNoteIcon sx={{ color: '#534AB7', fontSize: 20 }} />
                      </Box>

                      <Box
                        flex={1}
                        minWidth={0}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                          alignSelf: 'center',
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            // gap: 0.5,
                            overflow: 'hidden',
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{
                              textTransform: 'uppercase',
                              letterSpacing: '0.4px',
                            }}
                          >
                            IDENTIFICADOR:
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{ ml: 0.5, pb: 0.25 }}
                          >
                            {prova.idProva}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            // gap: 0.5,
                            overflow: 'hidden',
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{
                              textTransform: 'uppercase',
                              letterSpacing: '0.4px',
                            }}
                          >
                            PROVA:
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{ ml: 0.5, pb: 0.25 }}
                          >
                            {prova.descProva.toUpperCase()}
                          </Typography>
                        </Box>
                      </Box>

                      <ChevronRightIcon
                        sx={{ color: 'text.disabled', alignSelf: 'center' }}
                      />
                    </Box>

                    <Divider />

                    {/* Meta row */}
                    <Box
                      display="flex"
                      alignItems="center"
                      flexWrap="wrap"
                      gap={2}
                    >
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <PersonIcon
                          sx={{ fontSize: 14, color: 'text.disabled' }}
                        />
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          sx={{
                            textTransform: 'uppercase',
                            letterSpacing: '0.4px',
                          }}
                        >
                          Estudante:
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          sx={{ ml: 0.5, pb: 0.25 }}
                        >
                          {prova.nomeServidor}
                        </Typography>
                      </Box>

                      <Chip
                        size="small"
                        label={`${prova.qtdeQuestao} QUESTÕES`}
                        sx={{
                          pl: 1.5,
                          pr: 1.5,
                          pt: 0.3,
                          ml: 'auto',
                          bgcolor: '#EEEDFE',
                          color: '#534AB7',
                          fontWeight: 500,
                          fontSize: 11,
                          height: 22,
                        }}
                      />
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
        </Box>
      )}
    </Box>
  );
};

// import {
//   Box,
//   Card,
//   CardActionArea,
//   CardContent,
//   CircularProgress,
//   Divider,
//   Grid,
//   Typography,
// } from '@mui/material';
// import * as React from 'react';
// import { ListProvasRespondidasProps } from '../../questionnaire/types/questionnaire.types';

// interface ProvaSelectorProps {
//   provas: ListProvasRespondidasProps[];
//   isLoading: boolean;
//   onSelect: (eventUId: string) => void;
// }

// export const ProvaSelector: React.FC<ProvaSelectorProps> = ({
//   provas,
//   isLoading,
//   onSelect,
// }) => {
//   if (isLoading) {
//     return (
//       <Box
//         display="flex"
//         justifyContent="center"
//         alignItems="center"
//         minHeight="50vh"
//       >
//         <CircularProgress />
//       </Box>
//     );
//   }

//   return (
//     <Box sx={{ mt: 14, mb: 4 }}>
//       <Box
//         sx={{
//           background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
//           borderRadius: 2,
//           p: 1,
//           mb: 3,
//           mx: 2,
//           textAlign: 'center',
//         }}
//       >
//         <Typography
//           sx={{
//             color: '#ffffff',
//             fontSize: { xs: '11pt', md: '12pt' },
//             fontFamily: 'Roboto-Bold !important',
//             letterSpacing: '1px',
//             textTransform: 'uppercase',
//           }}
//         >
//           Visualização das Respostas
//         </Typography>
//       </Box>

//       {provas.length === 0 ? (
//         <Box textAlign="center" py={4} sx={{ mx: 2 }}>
//           <Typography variant="body2" color="text.secondary">
//             Nenhuma prova encontrada.
//           </Typography>
//         </Box>
//       ) : (
//         <Grid container spacing={1.5}>
//           {provas.map((prova) => (
//             <Grid item xs={12} key={prova.eventUId}>
//               <Card
//                 variant="outlined"
//                 sx={{
//                   mt: 2,
//                   mx: 2,
//                   borderRadius: 2,
//                   transition: 'border-color 0.15s',
//                   '&:hover': { borderColor: 'text.primary' },
//                 }}
//               >
//                 <CardActionArea onClick={() => onSelect(prova.eventUId)}>
//                   <CardContent
//                     sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
//                   >
//                     <Box flex={1}>
//                       <Typography variant="subtitle1" fontWeight={500}>
//                         {prova.descProva}
//                       </Typography>

//                       <Divider sx={{ my: 1 }} />

//                       <Box display="flex" gap={3} flexWrap="wrap">
//                         <Box>
//                           <Typography
//                             variant="caption"
//                             color="text.disabled"
//                             sx={{
//                               textTransform: 'uppercase',
//                               letterSpacing: '0.5px',
//                             }}
//                           >
//                             Servidor
//                           </Typography>
//                           <Typography variant="body2" color="text.secondary">
//                             {prova.nomeServidor}
//                           </Typography>
//                         </Box>

//                         <Box>
//                           <Typography
//                             variant="caption"
//                             color="text.disabled"
//                             sx={{
//                               textTransform: 'uppercase',
//                               letterSpacing: '0.5px',
//                             }}
//                           >
//                             Qtde. de questões
//                           </Typography>
//                           <Typography variant="body2" color="text.secondary">
//                             {prova.qtdeQuestao} QUESTÕES
//                           </Typography>
//                         </Box>
//                       </Box>
//                     </Box>

//                     <Typography
//                       color="text.disabled"
//                       sx={{ fontSize: 20, alignSelf: 'center' }}
//                     >
//                       ›
//                     </Typography>
//                   </CardContent>
//                 </CardActionArea>
//               </Card>
//             </Grid>
//           ))}
//         </Grid>
//       )}
//     </Box>
//   );
// };
